import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    OnInit,
    computed,
    inject,
    input,
    output,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import {
    IconButtonComponent,
    InputSearchComponent,
} from '@interacta-shared/ui';
import { isDefined } from '@interacta-shared/util';
import { ICommunity, IWorkspace } from '@interacta/util-community';
import { dashboardCommunity } from '@modules/app-routing/routes';
import { AppSelectors, AppState } from '@modules/core/store';
import { filterWorkspaces } from '@modules/sidebar/models/sidebar.utils';
import { CacheMap, WorkspaceUI } from '@modules/state/models/ui-state.model';
import { UIStateService } from '@modules/state/services/ui-state.service';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { concat, of } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
} from 'rxjs/operators';
import { SidebarWorkspaceListComponent } from '../sidebar-workspace-list/sidebar-workspace-list.component';

@Component({
    selector:
        'interacta-sidebar-advanced-community-research[workspaces][selectedCommunity]',
    templateUrl: './sidebar-advanced-community-research.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        InputSearchComponent,
        IconButtonComponent,
        SidebarWorkspaceListComponent,
        TranslatePipe,
    ],
})
export class SidebarAdvancedCommunityResearchComponent implements OnInit {
    private uiService = inject(UIStateService);
    private store = inject(Store<AppState>);
    private destroyRef = inject(DestroyRef);

    readonly workspaces =
        input.required<Pick<IWorkspace, 'id' | 'name' | 'communities'>[]>();
    readonly selectedCommunity = input.required<ICommunity | null>();
    readonly uiStates = input.required<CacheMap<WorkspaceUI> | null>();
    readonly collapsedWorkspaceList = input<number[]>([]);
    readonly expanded = input(false);
    readonly showDashboardLink = input(false);

    readonly collapsedWs = output<number>();

    readonly searchInput = viewChild<InputSearchComponent>('searchInput');

    readonly communityFilter = new FormControl<string>('', {
        nonNullable: true,
    });

    readonly communityFilterValue = toSignal(
        concat(
            of(this.communityFilter.value),
            this.communityFilter.valueChanges.pipe(
                debounceTime(300),
                distinctUntilChanged(),
                map((s) => (isDefined(s) ? s : '')),
            ),
        ),
        { initialValue: '' },
    );

    readonly filteredData = computed(() => {
        const nameFilter = this.communityFilterValue();
        const workspaces = this.workspaces();

        return workspaces
            .map((workspace) =>
                filterWorkspaces(workspace, nameFilter, true, false),
            )
            .filter((workspace) => (workspace?.communities?.length ?? 0) > 0);
    });

    ngOnInit(): void {
        this.store
            .select(AppSelectors.selectRouteState)
            .pipe(
                filter(
                    (route) =>
                        isDefined(route) &&
                        route.appRoute !== dashboardCommunity,
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((_) => this.communityFilter.setValue(''));
    }

    openSidebarAndFocusInput(): void {
        this.uiService.changeLeftMenuOpen(true);

        setTimeout(() => this.searchInput()?.focus(), 300);
    }
}
